// Reads broadcast, sequence, and form email content without assuming a Quill
// instance is mounted. The standard editor is TipTap (ref "studioEditor").
// Older screens still host Quill at basicEmailEditor.quill. Missing refs must
// not throw: wizard Back, Done Editing, and Next all call this on the way out
// of the content step.

function unavailable() {
  return { available: false, text: '', html: '' };
}

function firstRef(owner, name) {
  if (!owner || typeof owner !== 'object' || owner.$refs == null) return null;
  const value = owner.$refs[name];
  if (Array.isArray(value)) return value.length ? value[0] : null;
  return value || null;
}

export function plainTextFromHtml(html) {
  return String(html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr|blockquote|section)>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"');
}

export function isEmptyEmailText(text) {
  return !String(text == null ? '' : text).replace(/\u00a0/g, ' ').trim();
}

export function htmlIsEmpty(html) {
  const source = String(html || '');
  if (/<(img|picture|svg|table|hr|video|audio|iframe|object|embed)\b/i.test(source)) {
    return false;
  }
  return isEmptyEmailText(plainTextFromHtml(source));
}

function readingFromParts(text, html) {
  return {
    available: true,
    text: text == null ? '' : String(text),
    html: html == null ? '' : String(html)
  };
}

function readFromMethods(component) {
  let text = null;
  let html = null;
  try {
    if (typeof component.getPlainText === 'function') text = component.getPlainText();
  } catch (error) {
    return unavailable();
  }
  try {
    if (typeof component.getHtml === 'function') html = component.getHtml();
  } catch (error) {
    return unavailable();
  }
  if (text == null && html == null) return unavailable();
  if (text == null) text = plainTextFromHtml(html);
  if (html == null) html = '';
  return readingFromParts(text, html);
}

function readLegacyQuill(legacy) {
  if (!legacy || typeof legacy !== 'object') return unavailable();
  if (typeof legacy.getPlainText === 'function' || typeof legacy.getHtml === 'function') {
    return readFromMethods(legacy);
  }
  const quillHost = firstRef(legacy, 'basicEmailEditor') || firstRef(legacy, 'editor');
  const quill = quillHost && quillHost.quill;
  if (quill && typeof quill.getText === 'function') {
    let text = '';
    let html = '';
    try {
      text = quill.getText();
      html = quill.root && typeof quill.root.innerHTML === 'string' ? quill.root.innerHTML : '';
    } catch (error) {
      return unavailable();
    }
    return readingFromParts(text, html);
  }
  const editorEl = firstRef(legacy, 'editor');
  if (editorEl && typeof editorEl.outerText === 'string') {
    return readingFromParts(
      editorEl.outerText,
      typeof editorEl.innerHTML === 'string' ? editorEl.innerHTML : ''
    );
  }
  return unavailable();
}

function readStudio(studio) {
  if (!studio || typeof studio !== 'object') return unavailable();
  if (typeof studio.getPlainText === 'function' || typeof studio.getHtml === 'function') {
    return readFromMethods(studio);
  }
  const htmlMode = studio.mode && studio.mode !== 'Basic';
  if (studio.editor && typeof studio.editor.getText === 'function') {
    try {
      if (htmlMode) {
        const html = typeof studio.content === 'string' ? studio.content : '';
        return readingFromParts(plainTextFromHtml(html), html);
      }
      const text = studio.editor.getText();
      const html = typeof studio.editor.getHTML === 'function'
        ? studio.editor.getHTML()
        : (typeof studio.content === 'string' ? studio.content : '');
      return readingFromParts(text, html);
    } catch (error) {
      return unavailable();
    }
  }
  if (typeof studio.content === 'string') {
    return readingFromParts(plainTextFromHtml(studio.content), studio.content);
  }
  return unavailable();
}

export function readEditorContent(root, options = {}) {
  if (!root || typeof root !== 'object') return unavailable();
  if (!options.skipOwnMethods && (
    typeof root.getPlainText === 'function' || typeof root.getHtml === 'function'
  )) {
    return readFromMethods(root);
  }

  const studio = firstRef(root, 'studioEditor');
  if (studio) return readStudio(studio);

  const legacy = firstRef(root, 'basicEmailEditor');
  if (legacy) return readLegacyQuill(legacy);

  if (root.editor || (typeof root.content === 'string' && root.mode)) {
    return readStudio(root);
  }
  return unavailable();
}

export function syncEditorContent(root, assign) {
  try {
    const reading = readEditorContent(root);
    if (!reading.available || typeof assign !== 'function') return reading;
    const empty = isEmptyEmailText(reading.text) && htmlIsEmpty(reading.html);
    if (empty) assign('');
    else if (reading.html) assign(reading.html);
    return reading;
  } catch (error) {
    return unavailable();
  }
}
